// ============================================
// InstrumentEngine - Motor Multi-Instrumento
// Cuerdas: guitarra, ukelele · Teclado: piano, melodica
// ============================================

import {
  type NoteName,
  semitonesToNote,
  noteToSemitones,
} from "./guitarEngine";

export type FretboardInstrument = "guitar" | "ukulele";
export type KeyboardInstrument = "piano" | "melodica";
export type InstrumentType = FretboardInstrument | KeyboardInstrument;

const ALL_INSTRUMENTS: InstrumentType[] = ["guitar", "ukulele", "piano", "melodica"];

export function isInstrumentType(v: string): v is InstrumentType {
  return (ALL_INSTRUMENTS as string[]).includes(v);
}

export function isFretboardInstrument(i: InstrumentType): i is FretboardInstrument {
  return i === "guitar" || i === "ukulele";
}

export function isKeyboardInstrument(i: InstrumentType): i is KeyboardInstrument {
  return i === "piano" || i === "melodica";
}

export interface StringDef {
  stringNum: number; // 1=aguda, N=grave (N=6 para guitar, N=4 para uke)
  note: NoteName;
  octave: number;
  midi: number;
}

export interface InstrumentConfig {
  id: FretboardInstrument;
  name: string;
  strings: StringDef[]; // ordenado de grave a agudo para rendering físico
  fretCount: number;
  stringSpacing: number;
  nutWidth: number;
  scaleLength: number;
  targetBoardWidth: number;
  noteRadius: number;
  fontScale: number;
  paddingLeft: number;
  paddingTop: number;
  dotFrets: number[];
  doubleDotFrets: number[];
}

// ─── Guitarra ───
export const GUITAR_CONFIG: InstrumentConfig = {
  id: "guitar",
  name: "Guitarra",
  strings: [
    { stringNum: 6, note: "E", octave: 2, midi: 40 },
    { stringNum: 5, note: "A", octave: 2, midi: 45 },
    { stringNum: 4, note: "D", octave: 3, midi: 50 },
    { stringNum: 3, note: "G", octave: 3, midi: 55 },
    { stringNum: 2, note: "B", octave: 3, midi: 59 },
    { stringNum: 1, note: "E", octave: 4, midi: 64 },
  ],
  fretCount: 15,
  stringSpacing: 48,
  nutWidth: 10,
  scaleLength: 1800,
  targetBoardWidth: 1350,
  noteRadius: 19,
  fontScale: 1,
  paddingLeft: 80,
  paddingTop: 40,
  dotFrets: [3, 5, 7, 9, 12, 15],
  doubleDotFrets: [12],
};

// ─── Ukelele (Afinación Reentrant G4-C4-E4-A4) ───
// Orden físico en el mástil: G4 (arriba), C4, E4, A4 (abajo)
export const UKULELE_CONFIG: InstrumentConfig = {
  id: "ukulele",
  name: "Ukelele",
  strings: [
    { stringNum: 4, note: "G", octave: 4, midi: 67 },
    { stringNum: 3, note: "C", octave: 4, midi: 60 },
    { stringNum: 2, note: "E", octave: 4, midi: 64 },
    { stringNum: 1, note: "A", octave: 4, midi: 69 },
  ],
  fretCount: 15,
  stringSpacing: 56, // más espaciado por menos cuerdas
  nutWidth: 10,
  scaleLength: 1300, // escala más corta
  targetBoardWidth: 1200,
  noteRadius: 22, // puntos un poco más grandes
  fontScale: 1.1,
  paddingLeft: 80,
  paddingTop: 50,
  dotFrets: [5, 7, 10, 12],
  doubleDotFrets: [12],
};

export const INSTRUMENTS: Record<FretboardInstrument, InstrumentConfig> = {
  guitar: GUITAR_CONFIG,
  ukulele: UKULELE_CONFIG,
};

export const INSTRUMENT_OPTIONS: { id: InstrumentType; label: string; emoji: string }[] = [
  { id: "guitar", label: "Guitarra", emoji: "🎸" },
  { id: "ukulele", label: "Ukelele", emoji: "🪕" },
  { id: "piano", label: "Piano", emoji: "🎹" },
  { id: "melodica", label: "Melódica", emoji: "🎶" },
];

export const FRETBOARD_INSTRUMENT_OPTIONS = INSTRUMENT_OPTIONS.filter((o) =>
  isFretboardInstrument(o.id)
);

// ─── Helpers genéricos ───

/** Config de mástil; en teclado devuelve guitarra solo para cálculos internos legacy. */
export function getInstrumentConfig(type: InstrumentType): InstrumentConfig {
  if (isFretboardInstrument(type)) return INSTRUMENTS[type];
  return GUITAR_CONFIG;
}

export function getNoteAtPositionInstrument(
  stringNum: number,
  fret: number,
  instrument: InstrumentType = "guitar"
): { string: number; fret: number; note: NoteName; octave: number; midi: number } {
  const config = getInstrumentConfig(instrument);
  const stringDef = config.strings.find((s) => s.stringNum === stringNum);
  if (!stringDef) {
    // fallback para evitar crash
    return { string: stringNum, fret, note: "C", octave: 4, midi: 60 };
  }
  const midi = stringDef.midi + fret;
  const note = semitonesToNote(midi, false);
  const octave = stringDef.octave + Math.floor((stringDef.midi % 12 + fret) / 12);
  return { string: stringNum, fret, note, octave, midi };
}

export function findNotePositionsInstrument(
  targetNote: NoteName,
  instrument: InstrumentType = "guitar",
  frets: number = 15
): { string: number; fret: number; note: NoteName }[] {
  const config = getInstrumentConfig(instrument);
  const positions: { string: number; fret: number; note: NoteName }[] = [];
  for (const s of config.strings) {
    for (let f = 0; f <= frets; f++) {
      const pos = getNoteAtPositionInstrument(s.stringNum, f, instrument);
      if (noteToSemitones(pos.note) === noteToSemitones(targetNote)) {
        positions.push({ string: s.stringNum, fret: f, note: pos.note });
      }
    }
  }
  return positions;
}
